import {
	ChevronRightIcon,
	EllipsisVerticalIcon,
} from "@heroicons/react/24/outline";
import {
	Activity,
	Award,
	Bell,
	Bookmark,
	Check,
	Feather,
	type LucideIcon,
	Pencil,
	RotateCcw,
	Thermometer,
	Trash2,
	Upload,
} from "lucide-react";
import { type CSSProperties, useEffect, useRef, useState } from "react";
import {
	resolveTaskDomain,
	TASK_DOMAIN_COLORS,
	TASK_DOMAIN_TINT_COLORS,
} from "../../../lib/journey/taskDomainColors";
import type { JourneyTask } from "../../../types/journey";
import styles from "./TaskCard.module.css";

export interface TaskCardProps {
	task: JourneyTask;
	view: "list" | "grid";
	onAskEve: (task: JourneyTask) => void;
	onChevronClick: (task: JourneyTask) => void;
	askEveLabel: string;
	progressLabel?: (count: number, total: number, unit: string) => string;
	/** Optional — when provided, the overflow menu offers "Mark as complete". */
	onMarkComplete?: () => void;
	/** Optional — when provided, the overflow menu offers "Mark as incomplete". */
	onMarkIncomplete?: () => void;
	/** Optional — when provided, the overflow menu offers "Edit" (custom tasks). */
	onEdit?: () => void;
	/** Optional — when provided, the overflow menu offers "Delete" (custom tasks). */
	onDelete?: () => void;
	editLabel?: string;
	deleteLabel?: string;
	moreOptionsLabel?: string;
	/** aria-label for the list-view card (whole card opens the detail). */
	openLabel?: string;
	markCompleteLabel?: string;
	markIncompleteLabel?: string;
	/** When true, never render the progress block (D2C cards). */
	hideProgress?: boolean;
	/** When true, render the narrower D2C card width. */
	compact?: boolean;
	/** When true, the card fills its container width (Get Ready list) instead of
	 * the fixed carousel width. */
	fullWidth?: boolean;
	/** Topfill label shown instead of the deadline on completed cards. */
	completedOnTimeLabel?: string;
	/** Pill label shown instead of Ask Eve on completed cards. */
	completedLabel?: string;
}

const ACTION_ICON: Record<string, LucideIcon> = {
	fill: Feather,
	upload: Upload,
	book: Bookmark,
	"review-sign": Award,
	test: Thermometer,
	notify: Bell,
	monitor: Activity,
};

export function TaskCard({
	task,
	view,
	onAskEve,
	onChevronClick,
	askEveLabel,
	progressLabel,
	onMarkComplete,
	onMarkIncomplete,
	onEdit,
	onDelete,
	editLabel,
	deleteLabel,
	moreOptionsLabel,
	openLabel,
	markCompleteLabel,
	markIncompleteLabel,
	hideProgress,
	compact,
	fullWidth,
	completedOnTimeLabel = "Completed on time!",
	completedLabel = "Completed",
}: TaskCardProps) {
	const ActionIcon = ACTION_ICON[task.iconName ?? "fill"] ?? Feather;
	const showProgress =
		typeof task.progressTotal === "number" && task.progressTotal > 0;
	const pct = typeof task.progress === "number" ? task.progress : 0;
	const completedCount = showProgress
		? Math.round((pct / 100) * (task.progressTotal ?? 0))
		: 0;
	const progressUnit = task.progressUnit ?? "items";

	const isCompleted = task.status === "completed";
	const canMarkComplete = !isCompleted && typeof onMarkComplete === "function";
	const canMarkIncomplete =
		isCompleted && typeof onMarkIncomplete === "function";
	const canEdit = typeof onEdit === "function";
	const canDelete = typeof onDelete === "function";
	const hasMenu = canMarkComplete || canMarkIncomplete || canEdit || canDelete;

	const [menuOpen, setMenuOpen] = useState(false);
	const menuContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		if (!menuOpen) return;
		function handleDocClick(event: MouseEvent) {
			const node = menuContainerRef.current;
			if (
				node &&
				event.target instanceof Node &&
				!node.contains(event.target)
			) {
				setMenuOpen(false);
			}
		}
		function handleEsc(event: KeyboardEvent) {
			if (event.key === "Escape") setMenuOpen(false);
		}
		document.addEventListener("mousedown", handleDocClick);
		document.addEventListener("keydown", handleEsc);
		return () => {
			document.removeEventListener("mousedown", handleDocClick);
			document.removeEventListener("keydown", handleEsc);
		};
	}, [menuOpen]);

	const domain = resolveTaskDomain(task.taskDomain);
	const domainColor = TASK_DOMAIN_COLORS[domain];
	const domainTint = TASK_DOMAIN_TINT_COLORS[domain];

	// In list view the bottom action row (with the chevron) is hidden, so the
	// whole card acts as the open-detail target — same as the grid chevron.
	const isList = view === "list";
	const openDetail = () => onChevronClick(task);

	return (
		<article
			className={styles.card}
			data-category={task.category}
			data-compact={compact ? "true" : undefined}
			data-full-width={fullWidth ? "true" : undefined}
			data-completed={isCompleted ? "true" : undefined}
			data-d2c={hideProgress ? "true" : undefined}
			data-domain={domain}
			data-view={view}
			onClick={isList ? openDetail : undefined}
			onKeyDown={
				isList
					? (event) => {
							if (event.key === "Enter" || event.key === " ") {
								event.preventDefault();
								openDetail();
							}
						}
					: undefined
			}
			role={isList ? "button" : undefined}
			tabIndex={isList ? 0 : undefined}
			aria-label={isList ? (openLabel ?? `Open ${task.title}`) : undefined}
			style={
				{
					"--task-domain-color": domainColor,
					"--task-domain-tint": domainTint,
				} as CSSProperties
			}
		>
			<div className={styles.topfill}>
				<span className={styles.deadline}>
					{/* dueDateLabel is the localized display string; deadline is the
					    English machine key the CDN keeps identical across locales. */}
					{isCompleted
						? completedOnTimeLabel
						: task.dueDateLabel || task.deadline}
				</span>
			</div>

			<div className={styles.headlineRow}>
				{isCompleted ? (
					<span className={styles.completedCheck} aria-hidden="true">
						<Check className={styles.completedCheckIcon} strokeWidth={3} />
					</span>
				) : (
					<ActionIcon
						className={styles.icon}
						strokeWidth={1.5}
						aria-hidden="true"
					/>
				)}
				<h3 className={styles.title}>{task.title}</h3>
			</div>

			<p className={styles.description}>
				{task.sevenWordIntro ?? task.description}
			</p>

			<div className={styles.footer}>
				{showProgress && !hideProgress ? (
					<div className={styles.progressBlock}>
						<div className={styles.progressLabelRow}>
							<span className={styles.progressLabel}>
								{progressLabel
									? progressLabel(
											completedCount,
											task.progressTotal ?? 0,
											progressUnit,
										)
									: `${completedCount} of ${task.progressTotal} ${progressUnit} completed`}
							</span>
							<span className={styles.progressPct}>{pct}%</span>
						</div>
						<div
							className={styles.progressTrack}
							role="progressbar"
							aria-valuenow={pct}
							aria-valuemin={0}
							aria-valuemax={100}
						>
							<div
								className={styles.progressFill}
								style={
									{
										"--progress-pct": `${pct}%`,
									} as CSSProperties
								}
							/>
						</div>
					</div>
				) : null}

				<div className={styles.bottomRow}>
					<div className={styles.bottomLeft}>
						<div className={styles.moreWrapper} ref={menuContainerRef}>
							<button
								type="button"
								className={styles.moreButton}
								aria-label={
									moreOptionsLabel ?? `More options for ${task.title}`
								}
								aria-haspopup={hasMenu ? "true" : undefined}
								aria-expanded={hasMenu ? menuOpen : undefined}
								disabled={!hasMenu}
								aria-disabled={!hasMenu}
								onClick={
									hasMenu ? () => setMenuOpen((open) => !open) : undefined
								}
							>
								<EllipsisVerticalIcon
									width={20}
									height={20}
									aria-hidden="true"
								/>
							</button>

							{hasMenu && menuOpen ? (
								<div className={styles.menu}>
									{canMarkComplete ? (
										<button
											type="button"
											className={styles.menuItem}
											onClick={() => {
												setMenuOpen(false);
												onMarkComplete?.();
											}}
										>
											<Check
												className={styles.menuItemIcon}
												aria-hidden="true"
											/>
											<span>{markCompleteLabel ?? "Mark as complete"}</span>
										</button>
									) : null}
									{canMarkIncomplete ? (
										<button
											type="button"
											className={styles.menuItem}
											onClick={() => {
												setMenuOpen(false);
												onMarkIncomplete?.();
											}}
										>
											<RotateCcw
												className={styles.menuItemIcon}
												aria-hidden="true"
											/>
											<span>{markIncompleteLabel ?? "Mark as incomplete"}</span>
										</button>
									) : null}
									{canEdit ? (
										<button
											type="button"
											className={styles.menuItem}
											onClick={() => {
												setMenuOpen(false);
												onEdit?.();
											}}
										>
											<Pencil
												className={styles.menuItemIcon}
												aria-hidden="true"
											/>
											<span>{editLabel ?? "Edit"}</span>
										</button>
									) : null}
									{canDelete ? (
										<button
											type="button"
											className={`${styles.menuItem} ${styles.menuItemDelete}`}
											onClick={() => {
												setMenuOpen(false);
												onDelete?.();
											}}
										>
											<Trash2
												className={styles.menuItemIcon}
												aria-hidden="true"
											/>
											<span>{deleteLabel ?? "Delete"}</span>
										</button>
									) : null}
								</div>
							) : null}
						</div>

						{isCompleted ? (
							<span className={styles.completedPill}>
								<Check
									className={styles.completedPillIcon}
									strokeWidth={3}
									aria-hidden="true"
								/>
								<span>{completedLabel}</span>
							</span>
						) : (
							<button
								type="button"
								className={styles.askEveButton}
								onClick={() => onAskEve(task)}
								aria-label={askEveLabel}
							>
								<img
									src="/images/education/icon-ask-eve.svg"
									alt=""
									className={styles.askEveIcon}
									aria-hidden="true"
								/>
								<span>{askEveLabel}</span>
							</button>
						)}
					</div>

					<button
						type="button"
						className={styles.chevronButton}
						onClick={() => onChevronClick(task)}
						aria-label={`Open ${task.title}`}
					>
						<ChevronRightIcon
							className={styles.chevronIcon}
							aria-hidden="true"
						/>
					</button>
				</div>
			</div>
		</article>
	);
}
